// @ts-nocheck
/**
 * Per-user settings drawer — modeled on obd3-gui v1's UserConsole.
 * Opens from the right when the user clicks the row's Settings icon.
 *
 * Tabs (adapted for Cloud Telephony's action surface):
 *   • Overview — read-only meta (Username / Full name / Role / Email /
 *                Company / Call centre / ACL / Account type / Expiry /
 *                Status / Created by) + Edit + Switch + View hierarchy
 *                + View details.
 *   • Security — Show password (inline reveal) + Reset password (route).
 *   • Lifecycle — Block/Unblock + Delete (both via ConfirmDialog).
 *
 * All heavy lifting (API calls, existing Bootstrap modals in the parent)
 * is triggered via callbacks — this component just orchestrates the UI.
 */
import { useEffect, useState } from 'react';
import {
  Ban,
  CircleCheck,
  Eye,
  KeyRound,
  LogIn,
  Network,
  Pencil,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils/cn';
import { USER_ROLES, USERS_ROLE, BACKEND_API_BASE_URL } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { useAuthedImageUrl } from '@/hooks/useAuthedImageUrl';
import { storageUrlOr } from '@/utilities/storageUrl';
import { UserStatusBadge } from './UserStatusBadge';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: any;
  canRead: boolean;
  canEdit: boolean;
  canDelete: boolean;
  isSwitchingAccount: boolean;
  canSwitchAccount?: boolean;
  onEdit: () => void;
  onSwitchAccount: () => void;
  onShowPassword: () => void;
  onResetPassword: () => void;
  onBlockToggle: () => void;
  onDelete: () => void;
  onViewHierarchy: () => void;
}

function fmtDate(v?: string | null) {
  if (!v) return '—';
  const d = new Date(String(v).replace(' ', 'T'));
  if (!Number.isFinite(d.getTime())) return String(v);
  // Compact form (`12 Aug 2026, 15:14`) so the value fits inside a
  // single-column meta tile without truncation.
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function fmtAmount(v: any) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export function UserConsole({
  open,
  onOpenChange,
  user,
  canRead,
  canEdit,
  canDelete,
  isSwitchingAccount,
  canSwitchAccount = true,
  onEdit,
  onSwitchAccount,
  onShowPassword,
  onResetPassword,
  onBlockToggle,
  onDelete,
  onViewHierarchy,
}: Props) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState(false);

  const active = Number(user.status) === 1 || user.status === 'Active';
  const isBlocked = !active;
  const canSwitch = active && !isSwitchingAccount;
  const showSecurity = getUser()?.role === USER_ROLES.SUPERADMIN;
  const roleLabel = USERS_ROLE[user.role] || user.role || '—';

  const aclName =
    user?.acl_settings && typeof user.acl_settings === 'object'
      ? user.acl_settings.acl_name
      : null;
  const aclId = user?.acl_settings_id ?? user?.acl_settings?.id ?? null;
  const aclDisplay = aclName
    ? `${aclName}${aclId != null ? ` (ID ${aclId})` : ''}`
    : aclId != null
      ? `ID ${aclId}`
      : '—';

  const panelDisplayName = user?.effective_panel_display_name || null;
  const logoPath = user?.effective_logo_path || null;
  const logoBlobPath = user?.effective_logo_blob_path || null;
  const rawLogoUrl = logoPath
    ? storageUrlOr('logo', logoBlobPath, `${BACKEND_API_BASE_URL.replace('/api', '')}/${logoPath}`)
    : null;
  const { src: logoUrl } = useAuthedImageUrl(rawLogoUrl);
  const [logoBroken, setLogoBroken] = useState(false);
  // The sheet is reused across users — clear the error when the logo changes.
  useEffect(() => setLogoBroken(false), [logoUrl]);

  const meta: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'User ID', value: <span className="font-mono">{user.id}</span> },
    { label: 'Username', value: user.username || '—' },
    { label: 'Full name', value: user.name || '—' },
    { label: 'Role', value: roleLabel },
    { label: 'Email', value: user.email || '—' },
    {
      label: 'Company',
      value:
        user?.assignedCompany?.name ||
        user?.companies?.name ||
        user?.company_name ||
        user?.company?.name ||
        '—',
    },
    {
      label: 'Call centre',
      value:
        user?.assigned_call_center_details?.name ||
        user?.assignedCallCenter?.name ||
        user?.assignedCallCenter?.callcentre_name ||
        user?.callcenter?.name ||
        '—',
    },
    {
      label: 'Account type',
      value: Number(user.account_type) === 1 ? 'Transactional' : 'Promotional',
    },
    { label: 'ACL setting', value: aclDisplay },
    ...(panelDisplayName
      ? [{ label: 'Panel display name', value: panelDisplayName }]
      : []),
    ...(logoUrl && !logoBroken
      ? [
          {
            label: 'Logo',
            value: (
              <img
                src={logoUrl}
                alt={panelDisplayName || 'Panel logo'}
                className="h-9 w-9 rounded-full border border-border object-contain"
                onError={() => setLogoBroken(true)}
              />
            ),
          },
        ]
      : []),
    // {
    //   label: 'Voice credits',
    //   value: (
    //     <span className="font-mono tabular-nums">
    //       {fmtAmount(user?.assignedCompany?.credits_available)}
    //     </span>
    //   ),
    // },
    // {
    //   label: 'TTS credits',
    //   value: (
    //     <span className="font-mono tabular-nums">
    //       {fmtAmount(user?.assignedCompany?.tts_credits)}
    //     </span>
    //   ),
    // },
    { label: 'Joining date', value: fmtDate(user.created_at) },
    {
      label: 'Expiry',
      value:
        typeof user.expiry_date === 'string' && user.expiry_date.length >= 10
          ? user.expiry_date.slice(0, 10)
          : user.expiry_date || '—',
    },
    { label: 'Status', value: <UserStatusBadge user={user} /> },
    { label: 'Created by', value: user?.created_by?.username || '—' },
  ];

  const closeAnd = (fn: () => void) => () => {
    onOpenChange(false);
    fn();
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-xl">
          <SheetHeader className="pr-8">
            <div className="flex items-center gap-3">
              <SheetTitle className="truncate text-base">@{user.username}</SheetTitle>
              <UserStatusBadge user={user} />
              <Badge variant="secondary" className="h-5">
                {roleLabel}
              </Badge>
            </div>
            <SheetDescription className="truncate">
              {user.name}
              {user.email ? ` · ${user.email}` : ''}
            </SheetDescription>

            <div className="flex flex-wrap items-center gap-2 pt-2">
              {canEdit && (
                <Button size="sm" variant="outline" onClick={closeAnd(onEdit)}>
                  <Pencil className="h-4 w-4" />
                  Edit user
                </Button>
              )}
              {canSwitchAccount && (
              <Button
                size="sm"
                variant="outline"
                onClick={onSwitchAccount}
                disabled={!canSwitch}
                title={
                  !active
                    ? 'Cannot switch to a blocked user'
                    : isSwitchingAccount
                      ? 'Switching…'
                      : `Switch to ${user.username}`
                }
              >
                <LogIn className="h-4 w-4" />
                {isSwitchingAccount ? 'Switching…' : 'Switch to user'}
              </Button>
              )}
            </div>
          </SheetHeader>

          <Tabs
            defaultValue="overview"
            className="mt-4 flex min-h-0 flex-1 flex-col"
          >
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              {showSecurity && <TabsTrigger value="security">Security</TabsTrigger>}
              <TabsTrigger value="lifecycle">Lifecycle</TabsTrigger>
            </TabsList>

            <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
              <TabsContent value="overview">
                <section className="rounded-md border bg-card">
                  <header className="border-b px-4 py-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Account details
                  </header>
                  {/* Tile grid — label on top, value below. Each tile
                      grows to fit its content so long values (ACL name,
                      joining date) don't truncate, and every row keeps
                      a consistent height on either side. */}
                  <dl className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">
                    {meta.map((r) => (
                      <div
                        key={r.label}
                        className="rounded-md border border-border/60 bg-background/60 px-3 py-2"
                      >
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {r.label}
                        </dt>
                        <dd className="mt-0.5 break-words text-sm font-medium text-foreground">
                          {r.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>

                <section className="mt-4 rounded-md border bg-card">
                  <header className="flex items-center justify-between border-b px-4 py-3">
                    <div>
                      <h3 className="text-sm font-semibold">Hierarchy</h3>
                      <p className="text-xs text-muted-foreground">
                        Parent chain up to Super Admin.
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={closeAnd(onViewHierarchy)}>
                      <Network className="h-4 w-4" />
                      View hierarchy
                    </Button>
                  </header>
                </section>
              </TabsContent>

              {showSecurity && (
              <TabsContent value="security">
                {canRead && (
                  <section className="rounded-md border bg-card">
                    <header className="flex items-center justify-between border-b px-4 py-3">
                      <div>
                        <h3 className="text-sm font-semibold">Show password</h3>
                        <p className="text-xs text-muted-foreground">
                          Reveals the current password in a secure dialog.
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={closeAnd(onShowPassword)}>
                        <Eye className="h-4 w-4" />
                        Show password
                      </Button>
                    </header>
                  </section>
                )}

                {canEdit && (
                  <section className={cn('rounded-md border bg-card', canRead && 'mt-4')}>
                    <header className="flex items-center justify-between border-b px-4 py-3">
                      <div>
                        <h3 className="text-sm font-semibold">Reset password</h3>
                        <p className="text-xs text-muted-foreground">
                          Open the reset-password page for this user.
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={closeAnd(onResetPassword)}>
                        <KeyRound className="h-4 w-4" />
                        Reset password
                      </Button>
                    </header>
                  </section>
                )}

                {!canRead && !canEdit && (
                  <p className="rounded-md border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
                    No password permissions in your assigned ACL.
                  </p>
                )}
              </TabsContent>
              )}

              <TabsContent value="lifecycle">
                {canEdit && (
                  <section className="rounded-md border bg-card">
                    <header className="flex items-center justify-between border-b px-4 py-3">
                      <div>
                        <h3 className="text-sm font-semibold">
                          {isBlocked ? 'Unblock account' : 'Block account'}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {isBlocked
                            ? 'Re-enable sign-in for this user.'
                            : 'Prevents sign-in but preserves all data.'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant={isBlocked ? 'default' : 'outline'}
                        onClick={() => setConfirmBlock(true)}
                      >
                        {isBlocked ? (
                          <CircleCheck className="h-4 w-4" />
                        ) : (
                          <Ban className="h-4 w-4" />
                        )}
                        {isBlocked ? 'Unblock' : 'Block'}
                      </Button>
                    </header>
                  </section>
                )}

                {canDelete && (
                  <section className="mt-4 rounded-md border border-destructive/30 bg-destructive/5">
                    <header className="flex items-center justify-between border-b border-destructive/30 px-4 py-3">
                      <div>
                        <h3 className="text-sm font-semibold text-destructive">
                          Delete user
                        </h3>
                        <p className="text-xs text-destructive/80">
                          Removes the account permanently. Cannot be undone.
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setConfirmDelete(true)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete user
                      </Button>
                    </header>
                  </section>
                )}

                {!canEdit && !canDelete && (
                  <p className="rounded-md border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
                    No lifecycle permissions in your assigned ACL.
                  </p>
                )}
              </TabsContent>
            </div>
          </Tabs>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmBlock}
        onOpenChange={setConfirmBlock}
        title={isBlocked ? 'Unblock user?' : 'Block user?'}
        description={
          isBlocked ? (
            <>
              <span className="font-medium text-foreground">{user.username}</span> will
              be able to sign in again.
            </>
          ) : (
            <>
              <span className="font-medium text-foreground">{user.username}</span> will
              be unable to sign in until unblocked.
            </>
          )
        }
        confirmLabel={isBlocked ? 'Unblock' : 'Block'}
        tone={isBlocked ? 'default' : 'destructive'}
        onConfirm={() => {
          setConfirmBlock(false);
          onOpenChange(false);
          onBlockToggle();
        }}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete user?"
        description={
          <>
            <span className="font-medium text-foreground">{user.username}</span> will be
            permanently deleted. This cannot be undone.
          </>
        }
        confirmLabel="Delete user"
        tone="destructive"
        onConfirm={() => {
          setConfirmDelete(false);
          onOpenChange(false);
          onDelete();
        }}
      />
    </>
  );
}
